import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, Headphones } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { Reveal, Marquee } from "@/components/Layout";

const HERO_LINES = ["Air Power &", "Wash Systems", "Engineered", "to Perform."];

const CATS = [
  { slug: "air-compressors", name: "Air Compressors", img: "https://images.unsplash.com/photo-1563456020111-f02d832ae94c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", n: "01" },
  { slug: "car-wash-machines", name: "Car Wash Machines", img: "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", n: "02" },
  { slug: "compressor-spares", name: "Genuine Spares", img: "https://images.unsplash.com/photo-1567093322102-6bdd32fba67d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", n: "03" },
  { slug: "accessories", name: "Accessories", img: "https://images.unsplash.com/photo-1530124566582-a618bc2615dc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", n: "04" },
];

const CHAPTERS = [
  { n: "01", t: "Sourced for the trade", d: "We stock compressors and pressure systems trusted by workshops, garages and detailing studios — not disposable consumer gear." },
  { n: "02", t: "Genuine spares, always", d: "Piston kits, pressure switches, FRL units and hoses that actually fit. Keep your machines running for years, not months." },
  { n: "03", t: "Priced for business", d: "Direct trade pricing with transparent MRP. Buy online, request bulk quotes, and reorder in seconds." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then(({ data }) => setFeatured(data.slice(0, 8)))
      .catch((e) => {
        console.error("Failed to load featured products", e);
        setFeatured([]);
      });
  }, []);

  return (
    <>
      <Seo
        title="Indoforce — Air Compressors, Car Wash Machines & Spares Online"
        description="Buy industrial air compressors, high-pressure car wash machines, genuine spare parts and accessories online. Trade pricing, fast delivery across India."
        canonical={`${window.location.origin}/`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Store",
          name: "Indoforce",
          description: "Air compressors, car wash machines, spares and accessories.",
          url: window.location.origin,
        }}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden pb-16 pt-28">
        <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="Industrial compressor"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/30" />
        </motion.div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-xs uppercase tracking-[0.3em] text-[#E31E24] mb-6"
          >
            Industrial Air & Wash Systems
          </motion.p>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl lg:text-[7vw]">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.includes("Perform") ? (
                    <>to <span className="text-[#E31E24]">Perform.</span></>
                  ) : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-btn"
              className="btn-fill group inline-flex items-center gap-3 border border-[#E31E24] text-[#E31E24] hover:text-black px-8 py-4 uppercase text-sm font-bold tracking-widest"
            >
              Shop the range <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact" data-testid="hero-quote-btn" className="text-white/70 hover:text-white text-sm uppercase tracking-widest px-4 py-4">
              Request a bulk quote →
            </Link>
          </motion.div>
        </div>
      </section>

      <Marquee items={["Air Compressors", "Car Wash Machines", "Genuine Spares", "Trade Pricing", "Fast Delivery"]} />

      {/* Trust bar */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 grid sm:grid-cols-3 gap-6">
        {[
          { icon: Truck, t: "Pan-India Delivery", d: "Shipped to workshops nationwide" },
          { icon: ShieldCheck, t: "Genuine Parts", d: "Warranty-backed components" },
          { icon: Headphones, t: "Trade Support", d: "Talk to real technicians" },
        ].map((f, i) => (
          <Reveal key={i} delay={i * 0.1} className="flex items-center gap-4 border border-white/10 p-6">
            <f.icon className="text-[#E31E24]" size={28} />
            <div>
              <p className="font-display font-bold uppercase tracking-tight">{f.t}</p>
              <p className="text-white/50 text-sm">{f.d}</p>
            </div>
          </Reveal>
        ))}
      </section>

      {/* Categories */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-16">
        <Reveal className="flex items-end justify-between mb-12">
          <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight">Shop by category</h2>
          <Link to="/shop" className="hidden md:inline text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#E31E24]">View all →</Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {CATS.map((c, i) => (
            <Link key={c.slug} to={`/shop?category=${c.slug}`} data-testid={`home-cat-${c.slug}`}>
              <Reveal delay={i * 0.08}>
                <div className="group relative aspect-[3/4] overflow-hidden border border-white/10">
                  <img src={c.img} alt={c.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  <span className="absolute top-4 left-4 font-display text-white/30 text-2xl font-black">{c.n}</span>
                  <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                    <h3 className="font-display font-bold uppercase tracking-tight text-lg leading-tight">{c.name}</h3>
                    <ArrowRight className="text-[#E31E24] group-hover:translate-x-1 transition-transform" size={20} />
                  </div>
                </div>
              </Reveal>
            </Link>
          ))}
        </div>
      </section>

      {/* Manifesto chapters */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 border-t border-white/10">
        <Reveal><p className="text-xs uppercase tracking-[0.3em] text-[#E31E24] mb-4">Why Indoforce</p></Reveal>
        <div className="grid md:grid-cols-3 gap-px bg-white/10">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.n} delay={i * 0.1} className="bg-[#0A0A0A] p-8 md:p-10">
              <span className="font-display text-6xl md:text-7xl font-black text-white/10">{ch.n}</span>
              <h3 className="font-display text-xl font-bold uppercase tracking-tight mt-4 mb-3">{ch.t}</h3>
              <p className="text-white/50 leading-relaxed text-sm">{ch.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured products */}
      {featured.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-16">
          <Reveal className="flex items-end justify-between mb-12">
            <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight">Featured gear</h2>
            <Link to="/shop" className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#E31E24]">All products →</Link>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-16">
        <Reveal className="relative overflow-hidden border border-white/10 bg-[#111111] p-10 md:p-20">
          <div className="relative z-10 max-w-2xl">
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none">
              Kitting out a <span className="text-[#E31E24]">workshop?</span>
            </h2>
            <p className="text-white/60 mt-6 mb-8 leading-relaxed">
              Get volume pricing on compressors and car wash systems. Send us your requirement and our team will build you a quote.
            </p>
            <Link to="/contact" data-testid="cta-quote-btn" className="btn-fill inline-flex items-center gap-3 border border-[#E31E24] text-[#E31E24] hover:text-black px-8 py-4 uppercase text-sm font-bold tracking-widest">
              Request a quote <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
